// IE Hub - Budget/Financial Service
// Provides centralized API operations for budget management (Income, Assets, Expenses, Debts)

import { httpGet, httpPost, httpPut } from "../http";
import {
  budgetEndpoints,
  customerEndpoints,
  reportEndpoints,
} from "../endpoints";
import { femaCapture } from "../../fema/capture";
import type {
  BroadbandSupplierRecord,
  ButtonActionLogRequest,
  ButtonActionLogResponse,
  EnergyCompanyRecord,
  ExpenseDataRecord,
  ExpensesNoteData,
  ExpensesNoteResponse,
  ExpensesNoteUpdateRequest,
  ExpenseUpdateRequest,
  GuidanceNoteRecord,
  IncomeDataRecord,
  IncomeNoteData,
  IncomeNoteUpdateRequest,
  IncomeTriggerRecord,
  IncomeUpdateRequest,
  LandlineSupplierRecord,
  MobileSupplierRecord,
  RequestConfig,
  ServiceResponse,
} from "../types";
import { DashboardService } from "./dashboardService";

/**
 * Budget/Financial Service Class
 * Handles all budget-related API operations with proper error handling, retry logic, and monitoring
 */
export class BudgetService {
  // Cache for performance optimization
  private static incomeDataCache = new Map<
    string,
    { data: IncomeDataRecord[]; timestamp: number }
  >();
  private static assetDataCache = new Map<
    string,
    { data: any[]; timestamp: number }
  >();
  private static expenseDataCache = new Map<
    string,
    { data: ExpenseDataRecord[]; timestamp: number }
  >();
  private static guidanceNotesCache: {
    data: GuidanceNoteRecord[];
    timestamp: number;
  } | null = null;
  private static triggersCache: {
    data: IncomeTriggerRecord[];
    timestamp: number;
  } | null = null;
  private static readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  /**
   * Invalidate the cached income record for a customer. Call after a successful
   * income PUT so the next GET returns fresh data (the UW budget form relies on
   * this to survive logout/login within the same tab session).
   */
  static clearIncomeCache(customerId: string | number | null | undefined): void {
    if (customerId === null || customerId === undefined) return;
    this.incomeDataCache.delete(String(customerId));
  }

  /** Invalidate the cached asset record for a customer. */
  static clearAssetCache(customerId: string | number | null | undefined): void {
    if (customerId === null || customerId === undefined) return;
    this.assetDataCache.delete(String(customerId));
  }

  /** Invalidate the cached expense record for a customer. */
  static clearExpenseCache(customerId: string | number | null | undefined): void {
    if (customerId === null || customerId === undefined) return;
    this.expenseDataCache.delete(String(customerId));
  }

  /**
   * Generate a temporary Budget PDF (legacy parity)
   * Returns PDF as a Blob for direct download
   */
  static async generateBudgetPdf(
    customerId: string | number,
    meta?: { firstIeDate?: string; loginDate?: string; appURL?: string },
    config?: RequestConfig
  ): ServiceResponse<Blob> {
    try {
      // Request PDF as blob (binary data)
      const response = await httpGet<Blob>(
        reportEndpoints.generateBudgetPdf(customerId),
        {
          ...config,
          responseType: "blob",
        }
      );

      const pdfBlob = response.data;

      if (!pdfBlob || !(pdfBlob instanceof Blob)) {
        throw new Error("Failed to generate budget PDF");
      }

      return {
        success: true,
        data: pdfBlob,
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture("budget_pdf_generate_error", error, {
          category: "api",
          severity: "error",
          context: { customerId },
          skipSanitization: false,
          user: {},
        });
      }
      throw error;
    }
  }

  /**
   * Download Budget PDF directly in the browser
   * Generates the PDF and triggers browser download
   */
  static async downloadBudgetPdf(
    customerId: string | number,
    meta?: { firstIeDate?: string; loginDate?: string; appURL?: string },
    config?: RequestConfig
  ): Promise<void> {
    try {
      // Generate the PDF (returns Blob)
      const result = await this.generateBudgetPdf(customerId, meta, config);

      if (!result.success || !result.data) {
        throw new Error("Failed to generate PDF");
      }

      const blob = result.data;

      // Create download link and trigger download
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `budget-${customerId}.pdf`;
      document.body.appendChild(link);
      link.click();

      // Cleanup DOM and blob URL
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture("budget_pdf_download_error", error, {
          category: "api",
          severity: "error",
          context: { customerId },
          skipSanitization: false,
          user: {},
        });
      }
      throw error;
    }
  }

  /**
   * Get summary/count of presented vs answered questions for a customer
   * The exact response shape will be confirmed after initial integration.
   */
  static async getCustomerPresentedAnswered(
    customerId: string | number,
    config?: RequestConfig
  ): ServiceResponse<Record<string, unknown>> {
    try {
      const response = await httpGet<any>(
        customerEndpoints.getCustomerPresentedAnswered(customerId),
        config
      );

      const data: Record<string, unknown> = (response?.data?.data ??
        response?.data ??
        {}) as Record<string, unknown>;
      return {
        success: true,
        data,
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture("customer_presented_answered_error", error, {
          category: "api",
          severity: "warning",
          context: { customerId },
          skipSanitization: false,
          user: {},
        });
      }
      throw error;
    }
  }

  /**
   * Cleanup temporary Budget PDF file by fileName (legacy parity)
   */
  static async deleteBudgetPdf(
    fileName: string,
    config?: RequestConfig
  ): ServiceResponse<{ deleted: boolean }> {
    try {
      const payload = {
        data: { fileName },
        params: { executeCountSql: "N" },
      } as const;

      await httpPost<any>(reportEndpoints.deleteBudgetPdf(), payload, config);
      return {
        success: true,
        data: { deleted: true },
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture("budget_pdf_delete_error", error, {
          category: "api",
          severity: "warning",
          context: { fileName },
          skipSanitization: false,
          user: {},
        });
      }
      // Do not throw for cleanup failures; return best-effort
      return {
        success: false,
        data: { deleted: false },
        message:
          error instanceof Error ? error.message : "Failed to delete PDF",
        timestamp: new Date().toISOString(),
      } as any;
    }
  }

  /**
   * Send monthly budget summary report email to customer
   * Uses GET method to trigger email sending via API
   */
  static async emailBudget(
    customerId: string | number,
    config?: RequestConfig
  ): ServiceResponse<{ sent: boolean }> {
    try {
      if (!customerId) {
        throw new Error("Customer ID is required");
      }

      await httpGet<any>(reportEndpoints.emailBudget(customerId), config);
      return {
        success: true,
        data: { sent: true },
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture("budget_email_send_error", error, {
          category: "api",
          severity: "error",
          context: { customerId },
          skipSanitization: false,
          user: {},
        });
      }
      throw error;
    }
  }

  /** INCOME ENDPOINTS */
  /**
   * Get income data for a customer
   * @param customerId - The customer ID
   * @param config - Optional request configuration
   */
  static async getIncomeData(
    customerId: string | number,
    config?: RequestConfig
  ): ServiceResponse<IncomeDataRecord[]> {
    try {
      // Check cache first
      const cacheKey = customerId.toString();
      const cached = this.incomeDataCache.get(cacheKey);
      const now = Date.now();

      const skipCache = config?.skipCache === true;

      if (!skipCache && cached && now - cached.timestamp < this.CACHE_TTL) {
        return {
          success: true,
          data: cached.data,
          timestamp: new Date().toISOString(),
        };
      }

      const response = await httpGet<any>(
        budgetEndpoints.getIncomeData(customerId),
        config
      );

      // Handle both envelope and raw array formats
      const payload = (response as any)?.data;
      const dataArray: IncomeDataRecord[] | null = Array.isArray(payload)
        ? (payload as IncomeDataRecord[])
        : (payload?.data ?? null);

      if (Array.isArray(dataArray)) {
        this.incomeDataCache.set(cacheKey, {
          data: dataArray,
          timestamp: now,
        });
        return {
          success: true,
          data: dataArray,
          timestamp: new Date().toISOString(),
        };
      }

      throw new Error("Failed to fetch income data");
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture("budget_income_data_error", error, {
          category: "api",
          severity: "error",
          context: { customerId },
          skipSanitization: false,
          user: {},
        });
      }
      throw error;
    }
  }

  /**
   * Fetch energy companies with NI-specific filtering
   * @param options.isNorthernIreland - true for NI postcodes (returns NI suppliers), false for non-NI
   */
  static async fetchEnergyCompanies(
    options: { skipErrorCapture?: boolean; isNorthernIreland: boolean }
  ): Promise<ServiceResponse<EnergyCompanyRecord[]>> {
    const endpoint = customerEndpoints.listEnergyCompanies(options.isNorthernIreland);
    try {
      const response = await httpGet<EnergyCompanyRecord[]>(
        endpoint,
        {
          timeout: 10000,
          headers: { "Content-Type": "application/json" },
        }
      );

      // Backend wraps response as { success, data: [...] } — unwrap the inner data array
      const rawData = (response?.data as any)?.data ?? response?.data;
      const data: EnergyCompanyRecord[] = Array.isArray(rawData)
        ? rawData
        : [];

      return {
        success: true,
        data: data,
        message: "Energy Companies fetched successfully",
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      if (!options.skipErrorCapture) {
        femaCapture("energy_companies_fetch_error", error, {
          category: "api",
          severity: "warning",
          context: {
            endpoint,
            isNorthernIreland: options.isNorthernIreland,
          },
          skipSanitization: false,
          user: {},
        });
      }

      return {
        success: false,
        data: [],
        message:
          error instanceof Error
            ? error.message
            : "Failed to fetch energy companies",
        timestamp: new Date().toISOString(),
      };
    }
  }

  static async fetchBroadbandSuppliers(
    options: { skipErrorCapture?: boolean } = {}
  ): Promise<ServiceResponse<BroadbandSupplierRecord[]>> {
    try {
      const response = await httpGet<BroadbandSupplierRecord[]>(
        customerEndpoints.listBroadbandSuppliers,
        {
          timeout: 10000,
          headers: { "Content-Type": "application/json" },
        }
      );

      // Backend wraps response as { success, data: [...] } — unwrap the inner data array
      const rawBb = (response?.data as any)?.data ?? response?.data;
      const data: BroadbandSupplierRecord[] = Array.isArray(rawBb)
        ? rawBb
        : [];

      return {
        success: true,
        data: data,
        message: "Broadband Suppliers fetched successfully",
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      if (!options.skipErrorCapture) {
        femaCapture("broadband_suppliers_fetch_error", error, {
          category: "api",
          severity: "warning",
          context: {
            endpoint: customerEndpoints.listBroadbandSuppliers,
          },
          skipSanitization: false,
          user: {},
        });
      }

      return {
        success: false,
        data: [],
        message:
          error instanceof Error
            ? error.message
            : "Failed to fetch broadband suppliers",
        timestamp: new Date().toISOString(),
      };
    }
  }

  static async fetchLandlineSuppliers(
    options: { skipErrorCapture?: boolean } = {}
  ): Promise<ServiceResponse<LandlineSupplierRecord[]>> {
    try {
      const response = await httpGet<LandlineSupplierRecord[]>(
        customerEndpoints.listLandlineSuppliers,
        {
          timeout: 10000,
          headers: { "Content-Type": "application/json" },
        }
      );

      // Backend wraps response as { success, data: [...] } — unwrap the inner data array
      const rawLl = (response?.data as any)?.data ?? response?.data;
      const data: LandlineSupplierRecord[] = Array.isArray(rawLl)
        ? rawLl
        : [];

      return {
        success: true,
        data: data,
        message: "Landline Suppliers fetched successfully",
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      if (!options.skipErrorCapture) {
        femaCapture("landline_suppliers_fetch_error", error, {
          category: "api",
          severity: "warning",
          context: {
            endpoint: customerEndpoints.listLandlineSuppliers,
          },
          skipSanitization: false,
          user: {},
        });
      }

      return {
        success: false,
        data: [],
        message:
          error instanceof Error
            ? error.message
            : "Failed to fetch landline suppliers",
        timestamp: new Date().toISOString(),
      };
    }
  }

  static async fetchMobileSuppliers(
    options: { skipErrorCapture?: boolean } = {}
  ): Promise<ServiceResponse<MobileSupplierRecord[]>> {
    try {
      const response = await httpGet<MobileSupplierRecord[]>(
        customerEndpoints.listMobileSuppliers,
        {
          timeout: 10000,
          headers: { "Content-Type": "application/json" },
        }
      );

      // Backend wraps response as { success, data: [...] } — unwrap the inner data array
      const rawMob = (response?.data as any)?.data ?? response?.data;
      const data: MobileSupplierRecord[] = Array.isArray(rawMob)
        ? rawMob
        : [];

      return {
        success: true,
        data: data,
        message: "Mobile Suppliers fetched successfully",
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      if (!options.skipErrorCapture) {
        femaCapture("mobile_suppliers_fetch_error", error, {
          category: "api",
          severity: "warning",
          context: {
            endpoint: customerEndpoints.listMobileSuppliers,
          },
          skipSanitization: false,
          user: {},
        });
      }

      return {
        success: false,
        data: [],
        message:
          error instanceof Error
            ? error.message
            : "Failed to fetch mobile suppliers",
        timestamp: new Date().toISOString(),
      };
    }
  }

  static clearIncomeDataCache() {
    this.incomeDataCache.clear();
  }

  static clearExpenseDataCache() {
    this.expenseDataCache.clear();
  }

  /**
   * Update income data for a customer
   * @param updateData - The income data to update
   * @param config - Optional request configuration
   */
  static async updateIncomeData(
    updateData: IncomeUpdateRequest,
    config?: RequestConfig
  ): ServiceResponse<IncomeDataRecord> {
    try {
      // Validate mandatory fields
      if (
        !updateData.id ||
        !updateData.customerId ||
        !updateData.lastUpdatedBy ||
        !updateData.lastUpdateDate
      ) {
        throw new Error(
          "Missing mandatory fields: id, customerId, lastUpdatedBy, lastUpdateDate"
        );
      }

      const response = await httpPut<any>(
        budgetEndpoints.updateIncomeData(),
        updateData,
        config
      );

      const updatedData: IncomeDataRecord | undefined =
        response?.data?.data || response?.data;
      if (updatedData) {
        // Update cache with new data
        const cacheKey = updateData.customerId.toString();
        this.incomeDataCache.set(cacheKey, {
          data: [updatedData],
          timestamp: Date.now(),
        });

        // Invalidate related dashboard cache so summary/cards reflect latest income
        try {
          DashboardService.clearCache(updateData.customerId);
        } catch {
          // Dashboard cache invalidation failure should not break income update
        }

        return {
          success: true,
          data: updatedData,
          timestamp: new Date().toISOString(),
        };
      }

      throw new Error("Failed to update income data");
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture("budget_income_update_error", error, {
          category: "api",
          severity: "error",
          context: { customerId: updateData.customerId },
          skipSanitization: false,
          user: {},
        });
      }
      throw error;
    }
  }

  /**
   * Get income notes for a customer
   * @param customerId - The customer ID
   * @param config - Optional request configuration
   */
  static async getIncomeNotes(
    customerId: string | number,
    config?: RequestConfig
  ): ServiceResponse<IncomeNoteData[]> {
    try {
      const response = await httpGet<any>(
        budgetEndpoints.getIncomeNotes(customerId),
        config
      );

      const notesData: IncomeNoteData[] =
        response?.data?.data || response?.data || [];
      return {
        success: true,
        data: notesData,
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture("budget_income_notes_error", error, {
          category: "api",
          severity: "warning",
          context: { customerId },
          skipSanitization: false,
          user: {},
        });
      }
      throw error;
    }
  }

  /**
   * update income notes for a customer
   * @param updateData - The income notes to update
   * @param config - Optional request configuration
   */
  static async updateIncomeNotes(
    updateData: IncomeNoteUpdateRequest,
    config?: RequestConfig
  ): ServiceResponse<IncomeNoteData[]> {
    try {
      const response = await httpPut<any>(
        budgetEndpoints.updateIncomeNotes(),
        updateData,
        config
      );

      const notesData: IncomeNoteData[] =
        response?.data?.data || response?.data || [];
      return {
        success: true,
        data: notesData,
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture("budget_income_notes_update_error", error, {
          category: "api",
          severity: "warning",
          context: { customerId: updateData.customerId },
          skipSanitization: false,
          user: {},
        });
      }
      throw error;
    }
  }

  /** EXPENSE ENDPOINTS */

  /**
   * get expense data for a customer
   * @param customerId - The customer ID
   * @param config - Optional request configuration
   */
  static async getExpenseData(
    customerId: string | number,
    config?: RequestConfig
  ): ServiceResponse<ExpenseDataRecord[]> {
    try {
      // Check cache first
      const cacheKey = customerId.toString();
      const cached = this.expenseDataCache.get(cacheKey);
      const now = Date.now();

      const skipCache = config?.skipCache === true;

      if (!skipCache && cached && now - cached.timestamp < this.CACHE_TTL) {
        return {
          success: true,
          data: cached.data,
          timestamp: new Date().toISOString(),
        };
      }

      const response = await httpGet<any>(
        budgetEndpoints.getExpenseData(customerId),
        config
      );

      // Handle both envelope and raw array formats
      const payload = (response as any)?.data;
      const dataArray: ExpenseDataRecord[] | null = Array.isArray(payload)
        ? (payload as ExpenseDataRecord[])
        : (payload?.data ?? null);

      if (Array.isArray(dataArray)) {
        this.expenseDataCache.set(cacheKey, {
          data: dataArray,
          timestamp: now,
        });
        return {
          success: true,
          data: dataArray,
          timestamp: new Date().toISOString(),
        };
      }

      throw new Error("Failed to fetch expense data");
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture("budget_expense_data_error", error, {
          category: "api",
          severity: "error",
          context: { customerId },
          skipSanitization: false,
          user: {},
        });
      }
      throw error;
    }
  }

  /**
   * update expense data for a customer
   * @param updateData - The expense data to update
   * @param config - Optional request configuration
   */
  static async updateExpenseData(
    updateData: ExpenseUpdateRequest,
    config?: RequestConfig
  ): ServiceResponse<ExpenseDataRecord> {
    try {
      // Validate mandatory fields
      if (
        !updateData.id ||
        !updateData.customerId ||
        !updateData.lastUpdatedBy ||
        !updateData.lastUpdateDate
      ) {
        throw new Error(
          "Missing mandatory fields: id, customerId, lastUpdatedBy, lastUpdateDate"
        );
      }

      const response = await httpPut<any>(
        budgetEndpoints.updateExpenseData(),
        updateData,
        config
      );

      const updatedData: ExpenseDataRecord | undefined =
        response?.data?.data || response?.data;
      if (updatedData) {
        // Update cache with new data
        const cacheKey = updateData.customerId.toString();
        this.expenseDataCache.set(cacheKey, {
          data: [updatedData],
          timestamp: Date.now(),
        });

        // Invalidate related dashboard cache so summary/cards reflect latest expenses
        try {
          DashboardService.clearCache(updateData.customerId);
        } catch {
          // Dashboard cache invalidation failure should not break expense update
        }

        return {
          success: true,
          data: updatedData,
          timestamp: new Date().toISOString(),
        };
      }

      throw new Error("Failed to update expense data");
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture("budget_expense_update_error", error, {
          category: "api",
          severity: "error",
          context: { customerId: updateData.customerId },
          skipSanitization: false,
          user: {},
        });
      }
      throw error;
    }
  }

  /**
   * get expense notes for a customer
   * @param customerId - The customer ID
   * @param config - Optional request configuration
   */
  static async getExpenseNotes(
    customerId: string | number,
    config?: RequestConfig
  ): ServiceResponse<ExpensesNoteData[]> {
    try {
      const response = await httpGet<any>(
        budgetEndpoints.getExpenseNotes(customerId),
        config
      );

      const notesData: ExpensesNoteData[] =
        response?.data?.data || response?.data || [];
      return {
        success: true,
        data: notesData,
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture("budget_expense_notes_error", error, {
          category: "api",
          severity: "warning",
          context: { customerId },
          skipSanitization: false,
          user: {},
        });
      }
      throw error;
    }
  }

  /**
   * update expense notes for a customer
   * @param updateData - The expense notes to update
   * @param config - Optional request configuration
   */
  static async updateExpenseNotes(
    updateData: ExpensesNoteUpdateRequest,
    config?: RequestConfig
  ): ServiceResponse<ExpensesNoteResponse[]> {
    try {
      const response = await httpPut<any>(
        budgetEndpoints.updateExpenseNotes(),
        updateData,
        config
      );

      const notesData: ExpensesNoteResponse[] =
        response?.data?.data || response?.data || [];
      return {
        success: true,
        data: notesData,
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture("budget_expense_notes_update_error", error, {
          category: "api",
          severity: "warning",
          context: { customerId: updateData.customerId },
          skipSanitization: false,
          user: {},
        });
      }
      throw error;
    }
  }

  /**
   * Get asset data for a customer
   * @param customerId - The customer ID
   * @param config - Optional request configuration
   */
  static async getAssetData(
    customerId: string | number,
    config?: RequestConfig
  ): ServiceResponse<any[]> {
    try {
      // Check cache first
      const cacheKey = customerId.toString();
      const cached = this.assetDataCache.get(cacheKey);
      const now = Date.now();

      const skipCache = config?.skipCache === true;

      if (!skipCache && cached && now - cached.timestamp < this.CACHE_TTL) {
        return {
          success: true,
          data: cached.data,
          timestamp: new Date().toISOString(),
        };
      }

      const response = await httpGet<any>(
        budgetEndpoints.getAssetData(customerId),
        config
      );

      // Handle both envelope and raw array formats
      const payload = (response as any)?.data;
      const dataArray: any[] | null = Array.isArray(payload)
        ? payload
        : (payload?.data ?? null);

      if (Array.isArray(dataArray)) {
        this.assetDataCache.set(cacheKey, {
          data: dataArray,
          timestamp: now,
        });
        return {
          success: true,
          data: dataArray,
          timestamp: new Date().toISOString(),
        };
      }

      throw new Error("Failed to fetch asset data");
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture("budget_asset_data_error", error, {
          category: "api",
          severity: "error",
          context: { customerId },
          skipSanitization: false,
          user: {},
        });
      }
      throw error;
    }
  }

  /**
   * Update asset data for a customer
   * @param updateData - The asset data to update
   * @param config - Optional request configuration
   */
  static async updateAssetData(
    updateData: any,
    config?: RequestConfig
  ): ServiceResponse<any> {
    try {
      if (!updateData.customerId) {
        throw new Error("Customer ID is required");
      }

      const response = await httpPut<any>(
        budgetEndpoints.updateAssetData(),
        updateData,
        config
      );

      const updatedData: any = response?.data?.data || response?.data;
      if (updatedData) {
        // Update cache with new data
        const cacheKey = updateData.customerId.toString();
        this.assetDataCache.set(cacheKey, {
          data: [updatedData],
          timestamp: Date.now(),
        });

        return {
          success: true,
          data: updatedData,
          timestamp: new Date().toISOString(),
        };
      }

      throw new Error("Failed to update asset data");
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture("budget_asset_update_error", error, {
          category: "api",
          severity: "error",
          context: { customerId: updateData.customerId },
          skipSanitization: false,
          user: {},
        });
      }
      throw error;
    }
  }

  /**
   * Get asset notes for a customer
   * @param customerId - The customer ID
   * @param config - Optional request configuration
   */
  static async getAssetNotes(
    customerId: string | number,
    config?: RequestConfig
  ): ServiceResponse<any[]> {
    try {
      const response = await httpGet<any>(
        budgetEndpoints.getAssetNotes(customerId),
        config
      );

      const notesData: any[] = response?.data?.data || response?.data || [];
      return {
        success: true,
        data: notesData,
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture("budget_asset_notes_error", error, {
          category: "api",
          severity: "warning",
          context: { customerId },
          skipSanitization: false,
          user: {},
        });
      }
      throw error;
    }
  }

  /**
   * Update asset notes for a customer
   * @param updateData - The asset notes to update
   * @param config - Optional request configuration
   */
  static async updateAssetNotes(
    updateData: any,
    config?: RequestConfig
  ): ServiceResponse<any> {
    try {
      if (!updateData.customerId) {
        throw new Error("Customer ID is required");
      }

      const response = await httpPut<any>(
        budgetEndpoints.updateAssetNotes(),
        updateData,
        config
      );

      const updatedData: any = response?.data?.data || response?.data;
      return {
        success: true,
        data: updatedData || updateData,
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture("budget_asset_notes_update_error", error, {
          category: "api",
          severity: "warning",
          context: { customerId: updateData.customerId },
          skipSanitization: false,
          user: {},
        });
      }
      throw error;
    }
  }

  /**
   * Get guidance notes for form fields
   * @param config - Optional request configuration
   */
  static async getGuidanceNotes(
    config?: RequestConfig
  ): ServiceResponse<GuidanceNoteRecord[]> {
    try {
      // Check cache first
      const now = Date.now();
      if (
        this.guidanceNotesCache &&
        now - this.guidanceNotesCache.timestamp < this.CACHE_TTL
      ) {
        return {
          success: true,
          data: this.guidanceNotesCache.data,
          timestamp: new Date().toISOString(),
        };
      }

      const response = await httpGet<any>(
        budgetEndpoints.getGuidanceNotes(),
        config
      );

      const payload = (response as any)?.data;
      const notesArray: GuidanceNoteRecord[] | null = Array.isArray(payload)
        ? (payload as GuidanceNoteRecord[])
        : (payload?.data ?? null);

      if (Array.isArray(notesArray)) {
        this.guidanceNotesCache = {
          data: notesArray,
          timestamp: now,
        };
        return {
          success: true,
          data: notesArray,
          timestamp: new Date().toISOString(),
        };
      }

      // Treat empty array as success with no notes
      if (Array.isArray(payload) && payload.length === 0) {
        this.guidanceNotesCache = { data: [], timestamp: now };
        return { success: true, data: [], timestamp: new Date().toISOString() };
      }

      throw new Error("Failed to fetch guidance notes");
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture("budget_guidance_notes_error", error, {
          category: "api",
          severity: "warning",
          context: {},
          skipSanitization: false,
          user: {},
        });
      }
      throw error;
    }
  }

  /**
   * Get income triggers for validation
   * @param config - Optional request configuration
   */
  static async getIncomeTriggers(
    config?: RequestConfig
  ): ServiceResponse<IncomeTriggerRecord[]> {
    try {
      // Check cache first
      const now = Date.now();
      if (
        this.triggersCache &&
        now - this.triggersCache.timestamp < this.CACHE_TTL
      ) {
        return {
          success: true,
          data: this.triggersCache.data,
          timestamp: new Date().toISOString(),
        };
      }

      const response = await httpGet<any>(
        budgetEndpoints.getIncomeTriggers(),
        config
      );

      const expenseResp = await httpGet<any>(
        budgetEndpoints.getExpenseTriggers(),
        config
      );

      const payload = (response as any)?.data;
      const expensePayload = (expenseResp as any)?.data;

      const incomeTriggersArray: IncomeTriggerRecord[] | null = Array.isArray(
        payload
      )
        ? (payload as IncomeTriggerRecord[])
        : (payload?.data ?? null);

      const expenseTriggersArray: IncomeTriggerRecord[] | null = Array.isArray(
        expensePayload
      )
        ? (expensePayload as IncomeTriggerRecord[])
        : (expensePayload?.data ?? null);

      // Normalize trigger records so downstream logic can reliably group
      // triggers into income vs expense vs asset buckets.
      //
      // Some environments return `incomeType` only on the income trigger endpoint
      // but omit it for the expense trigger endpoint. Our warning engine and
      // trigger-context builder use `incomeType` to decide whether a trigger
      // should be evaluated against income or expense values.
      const normalizedIncomeTriggers: IncomeTriggerRecord[] = Array.isArray(
        incomeTriggersArray
      )
        ? (incomeTriggersArray as IncomeTriggerRecord[]).map((t: any) => ({
            ...t,
            incomeType: t?.incomeType ?? "Income",
          }))
        : [];

      const normalizedExpenseTriggers: IncomeTriggerRecord[] = Array.isArray(
        expenseTriggersArray
      )
        ? (expenseTriggersArray as IncomeTriggerRecord[]).map((t: any) => ({
            ...t,
            // Always force expense triggers to be grouped as Expense since these
            // records come from the expense-trigger endpoint.
            incomeType: "Expense",
          }))
        : [];

      const triggersArray = [
        ...normalizedIncomeTriggers,
        ...normalizedExpenseTriggers,
      ];

      if (Array.isArray(triggersArray)) {
        this.triggersCache = {
          data: triggersArray,
          timestamp: now,
        };
        return {
          success: true,
          data: triggersArray,
          timestamp: new Date().toISOString(),
        };
      }

      throw new Error("Failed to fetch income triggers");
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture("budget_income_triggers_error", error, {
          category: "api",
          severity: "warning",
          context: {},
          skipSanitization: false,
          user: {},
        });
      }
      throw error;
    }
  }

  /**
   * Log button action for analytics
   * @param actionData - The button action data to log
   * @param config - Optional request configuration
   */
  static async logButtonAction(
    actionData: ButtonActionLogRequest,
    config?: RequestConfig
  ): ServiceResponse<any> {
    try {
      const response = await httpPost<ButtonActionLogResponse>(
        budgetEndpoints.logButtonAction(),
        actionData,
        config
      );

      const logData = response?.data;
      if (logData) {
        return {
          success: true,
          data: logData,
          timestamp: new Date().toISOString(),
        };
      }

      throw new Error("Failed to log button action");
    } catch (error) {
      if (!config?.skipErrorCapture) {
        femaCapture("budget_button_action_log_error", error, {
          category: "api",
          severity: "warning",
          context: {
            pageName: actionData.pageName,
            subSection: actionData.subSection,
          },
          skipSanitization: false,
          user: {},
        });
      }
      throw error;
    }
  }

  /**
   * Clear all caches
   */
  static clearAllCaches(): void {
    this.incomeDataCache.clear();
    this.expenseDataCache.clear();
    this.guidanceNotesCache = null;
    this.triggersCache = null;
  }

  /**
   * Clear cache for specific customer
   * @param customerId - The customer ID to clear cache for
   */
  static clearCustomerCache(customerId: string | number): void {
    this.incomeDataCache.delete(customerId.toString());
  }

  /**
   * Get cached income data if available
   * @param customerId - The customer ID
   */
  static getCachedIncomeData(
    customerId: string | number
  ): IncomeDataRecord[] | null {
    const cacheKey = customerId.toString();
    const cached = this.incomeDataCache.get(cacheKey);
    const now = Date.now();

    if (cached && now - cached.timestamp < this.CACHE_TTL) {
      return cached.data;
    }

    return null;
  }
}

export default BudgetService;
